import React, { useState, useCallback } from 'react'
import styles from './MediaView.module.scss'
import { useQuery, useLazyQuery } from '@apollo/client'
import {
  MY_ACCESS_QUERY,
  MY_DELIVERIES_QUERY,
  GET_DELIVERY_QUERY,
  GET_DELIVERY_DOWNLOAD_QUERY,
} from '../query/gqlQueries'

import CloseIcon from '@mui/icons-material/Close'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import DownloadIcon from '@mui/icons-material/Download'
import RefreshIcon from '@mui/icons-material/Refresh'
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile'
import PlayCircleIcon from '@mui/icons-material/PlayCircle'

const IMAGE_STORE_URI = process.env.REACT_APP_IMAGE_STORE_URI
function storeUrl(url) {
  if (!url || !IMAGE_STORE_URI) return url
  try {
    const u = new URL(url)
    return IMAGE_STORE_URI.replace(/\/$/, '') + u.pathname + u.search
  } catch { return url }
}

function formatBytes(bytes) {
  if (!bytes || bytes < 0) return '0 B'
  const units = ['B', 'KB', 'MB', 'GB', 'TB']
  let i = 0, n = bytes
  while (n >= 1024 && i < units.length - 1) { n /= 1024; i++ }
  return `${n.toFixed(n >= 10 || i === 0 ? 0 : 2)} ${units[i]}`
}

function prettyDate(d) {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(d || '')) return d || ''
  const [y, m, day] = d.split('-').map(Number)
  return new Date(y, m - 1, day).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })
}

// What an external account sees: the work delivered to them, and nothing else.
//
// This is a separate view rather than a filtered version of the internal one,
// because the two have genuinely different jobs. Someone here wants to find
// what they were sent and get it onto their machine — not browse an archive.
// Every internal query refuses this account server-side regardless, so the
// separation is for clarity rather than for safety.
export default function ClientPortal({ setIsVisible }) {
  const [selectedId, setSelectedId] = useState(null)
  const [downloadHint, setDownloadHint] = useState(null)

  const { data: accessData } = useQuery(MY_ACCESS_QUERY, { fetchPolicy: 'cache-and-network' })
  const { data, loading, refetch } = useQuery(MY_DELIVERIES_QUERY, { fetchPolicy: 'cache-and-network' })
  const [fetchDetail, { data: detailData, loading: detailLoading }] =
    useLazyQuery(GET_DELIVERY_QUERY, { fetchPolicy: 'cache-and-network' })
  const [fetchDownload] = useLazyQuery(GET_DELIVERY_DOWNLOAD_QUERY, { fetchPolicy: 'network-only' })

  const access = accessData?.myAccess
  const deliveries = data?.myDeliveries || []
  const detail = detailData?.getDelivery
  const files = detail?.files || []

  const open = useCallback((id) => {
    setSelectedId(id)
    setDownloadHint(null)
    fetchDetail({ variables: { identity: id } })
  }, [fetchDetail])

  const handleDownload = useCallback(async () => {
    if (!selectedId) return
    try {
      const { data: d } = await fetchDownload({ variables: { identity: selectedId } })
      const dl = d?.getDeliveryDownload
      if (!dl || !dl.files.length) return
      const gb = (dl.totalBytes / 1073741824).toFixed(2)
      const lines = [
        `# ${dl.shoot} — ${dl.fileCount} files, ${gb} GB`,
        `# Links expire ${new Date(dl.expiresAt).toLocaleString()}.`,
        '#',
        '# Save this file, open Terminal in the folder you want the files in,',
        '# and run:',
        '#',
        '#     curl --parallel --parallel-max 4 -C - -K THIS_FILE',
        '',
      ]
      for (const f of dl.files) {
        lines.push(`url = "${storeUrl(f.url)}"`)
        lines.push(`output = "${f.output}"`)
        lines.push('')
      }
      const blob = new Blob([lines.join('\n')], { type: 'text/plain' })
      const a = document.createElement('a')
      a.href = URL.createObjectURL(blob)
      a.download = `${(dl.shoot || 'files').replace(/[^a-zA-Z0-9-_]+/g, '-').toLowerCase()}.curl`
      document.body.appendChild(a); a.click(); document.body.removeChild(a)
      setTimeout(() => URL.revokeObjectURL(a.href), 10000)
      setDownloadHint({ file: a.download, count: dl.fileCount, gb })
    } catch (err) {
      window.alert(err.message || 'Could not prepare the download')
    }
  }, [selectedId, fetchDownload])

  // ───────────────────── ONE DELIVERY ─────────────────────
  if (selectedId && detail) {
    const p = detail.delivery.properties
    return (
      <div className={styles.view}>
        <div className={styles.gridBg} />
        <button className={styles.closeBtn} onClick={() => setIsVisible(false)}><CloseIcon /></button>
        <div className={styles.container}>
          <header className={styles.header}>
            <button className={styles.backBtn} onClick={() => setSelectedId(null)}>
              <ArrowBackIcon fontSize="small" /> Your files
            </button>
            <div className={styles.titleGroup}>
              <div className={styles.label}>{p.project}</div>
              <h1 className={styles.title}>{p.name}</h1>
              {p.notes && <p className={styles.notes}>{p.notes}</p>}
            </div>
            <div className={styles.toolbar}>
              <button className={`${styles.iconBtn} ${styles.primaryBtn}`}
                disabled={!files.length} onClick={handleDownload}>
                <DownloadIcon fontSize="small" /> Download all
              </button>
            </div>
          </header>

          <div className={styles.detailMeta}>
            <div className={styles.metaRow}>
              <span className={styles.metaLabel}>Delivered</span>
              <span className={styles.metaValue}>{prettyDate(p.deliveredOn) || '—'}</span>
            </div>
            <div className={styles.metaRow}>
              <span className={styles.metaLabel}>Contents</span>
              <span className={styles.metaValue}>
                {p.fileCount} file{p.fileCount === 1 ? '' : 's'} · {formatBytes(p.totalBytes)}
              </span>
            </div>
          </div>

          {downloadHint && (
            <div className={styles.downloadPanel}>
              <div className={styles.downloadHead}>
                Saved <code>{downloadHint.file}</code> — {downloadHint.count} files, {downloadHint.gb} GB
              </div>
              <p>Open Terminal in the folder you want the files in, and run:</p>
              <pre className={styles.downloadCmd}>curl --parallel --parallel-max 4 -C - -K ~/Downloads/{downloadHint.file}</pre>
              <p className={styles.downloadNote}>
                Or download files one at a time with the arrow on each below.
                Links are good for 12 hours — come back any time for fresh ones.
              </p>
              <button className={styles.iconBtn} onClick={() => setDownloadHint(null)}>Dismiss</button>
            </div>
          )}

          <div className={styles.photoGrid}>
            {files.map((f) => (
              <div key={f.path} className={styles.photoCard}>
                <div className={styles.photoThumb}>
                  {f.thumbnailUrl ? (
                    <img src={storeUrl(f.thumbnailUrl)} alt={f.filename} loading="lazy"
                      onError={(e) => { e.currentTarget.style.opacity = 0.2 }} />
                  ) : (
                    <div className={styles.photoThumbPlaceholder}>
                      {f.kind === 'video' ? <PlayCircleIcon fontSize="small" /> : <InsertDriveFileIcon fontSize="small" />}
                      <span>{f.kind}</span>
                    </div>
                  )}
                </div>
                <div className={styles.photoMeta}>
                  <div className={styles.photoName} title={f.filename}>{f.filename}</div>
                  <div className={styles.photoStats}>
                    <span>{formatBytes(f.size)}</span>
                    <a className={styles.sidecarChip} href={storeUrl(f.downloadUrl)} download={f.filename}
                      title={`Download ${f.filename}`}>⬇</a>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    )
  }

  // ───────────────────── EVERYTHING SHARED WITH ME ─────────────────────
  const byProject = deliveries.reduce((acc, d) => {
    const k = d.properties.project || 'Files'
    ;(acc[k] = acc[k] || []).push(d)
    return acc
  }, {})

  return (
    <div className={styles.view}>
      <div className={styles.gridBg} />
      <button className={styles.closeBtn} onClick={() => setIsVisible(false)}><CloseIcon /></button>
      <div className={styles.container}>
        <header className={styles.header}>
          <div className={styles.titleGroup}>
            <div className={styles.label}>
              {access ? `Signed in as ${access.username}` : 'Shared with you'}
            </div>
            <h1 className={styles.title}>Your files</h1>
          </div>
          <div className={styles.toolbar}>
            <button className={styles.iconBtn} onClick={() => refetch()} title="Reload">
              <RefreshIcon fontSize="small" />
            </button>
          </div>
        </header>

        {(loading || detailLoading) && !deliveries.length && (
          <div className={styles.status}>Loading…</div>
        )}

        {!loading && !deliveries.length && (
          <div className={styles.emptyState}>
            <p>Nothing has been shared with you yet.</p>
            <p style={{ fontSize: '0.85rem', color: '#94a3b8' }}>
              When a set of files is ready it will appear here, and you can download it.
            </p>
          </div>
        )}

        {Object.keys(byProject).sort().map((proj) => (
          <div key={proj} className={styles.projectGroup}>
            <div className={styles.projectGroupHeader}>{proj}</div>
            <div className={styles.projectGrid}>
              {byProject[proj].map((d) => {
                const p = d.properties
                return (
                  <div key={d.identity} className={styles.projectCard} onClick={() => open(d.identity)}>
                    <div className={styles.projectCardHeader}>
                      <div className={styles.projectName}>{p.name}</div>
                    </div>
                    {p.notes && <div className={styles.projectNotes}>{p.notes}</div>}
                    <div className={styles.projectMeta}>
                      <span className={styles.slugTag}>
                        {p.fileCount} file{p.fileCount === 1 ? '' : 's'} · {formatBytes(p.totalBytes)}
                      </span>
                      <span>{prettyDate(p.deliveredOn)}</span>
                    </div>
                  </div>
                )
              })}
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}
